import { AIInputWithLoading } from "@/components/ui/ai-input-with-loading";
import { useChat } from "ai/react";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { MessageComponent } from "./Message";
import { MessageLoading } from "@/components/ui/loading";
import { Message } from "ai";
import { params } from "@/params";

const processMapper = {
  reading_message: "Going over your message",
  looking_for_sources: "Looking for relevant sources in the help center",
  potential_questions: "Searching",
  reviewing_relevant_sources: "Reviewing relevant sources that I found",
  looking_for_the_answer: "Searching the correct answer",
  drafting_response: "Drafting initial Response",
  typing: "Typing...",
  done: "Finished and sent response",
  none: "",
};

export const BadResponsePage = ({
  conversation,
  context,
  handleToolSuggestion,
}: {
  conversation: Message[];
  context: {
    kb: any[];
    tickets: any[];
    memories: any[];
    snippets: any[];
  };
  handleToolSuggestion: (tools: {
    suggestedMemories: any[];
    suggestedSnippets: any[];
  }) => Promise<string | null>;
}) => {
  const [step, setStep] = useState<{ step: string; metadata: any } | null>(
    null
  );
  const [steps, setSteps] = useState<any[]>([]);
  const [isTyping, setTyping] = useState(false);
  const [chatId, setChatId] = useState(String(new Date().valueOf()));
  const searchParams = new URLSearchParams(window.location.search);
  const tenant = searchParams.get("t");

  const bottomRef = useRef<HTMLDivElement>(null);
  const bottomStepsRef = useRef<HTMLDivElement>(null);

  const { messages, isLoading, setMessages, append, data } = useChat({
    id: chatId,
    api: `${params.AI_URL}/api/simulate-reply/improve`,
    body: {
      tenantId: tenant,
      oldConversation: conversation,
      publicKnowledgeData: context.kb,
      ticketsUsed: context.tickets,
      memories: context.memories,
      snippets: context.snippets,
    },
  });

  useEffect(() => {
    bottomStepsRef.current?.scrollIntoView();
  }, [steps.length]);

  useEffect(() => {
    if (data?.length && data[data.length - 1]) {
      setStep((data as any)[data.length - 1]);
      setSteps((prev) => [...prev, (data as any)[data.length - 1]]);

      if ((data as any)[data.length - 1].step === "typing") {
        setTyping(false);
      } else {
        setTyping(true);
      }
    }
  }, [data]);

  useEffect(() => {
    setSteps([]);
  }, [chatId]);

  useEffect(() => {
    setTimeout(() => {
      setMessages(() => {
        return [
          {
            id: "wrong-response",
            content: conversation?.[conversation.length - 1]?.content,
            role: "assistant",
          },
          {
            id: "intro",
            content: `Whats Wrong with this message`,
            role: "assistant",
          },
        ];
      });
    }, 300);
  }, [chatId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView();
  }, [messages]);

  useEffect(() => {
    if (messages.length % 2 === 0) {
      setSteps([]);
    }
  }, [messages.length]);

  const tools = useMemo(() => {
    if (messages?.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (
        lastMessage?.toolInvocations &&
        lastMessage.toolInvocations.length > 0
      ) {
        return lastMessage.toolInvocations.map((invocation: any) => {
          if (invocation.toolName === "snippetCreationTool") {
            return { type: "snippet", data: invocation.args.snippetData };
          } else if (invocation.toolName === "memoryCreationTool") {
            return {
              type: "memory",
              data: invocation.args.content,
              impact: invocation.args.impact,
            };
          }
          return invocation;
        });
      }
    }
    return [];
  }, [messages]);

  useEffect(() => {
    if (tools.length > 0) {
      const handleRetry = async () => {
        const res = await handleToolSuggestion({
          suggestedMemories: tools.filter(
            (tool: any) => tool.type === "memory"
          ),
          suggestedSnippets: tools.filter(
            (tool: any) => tool.type === "snippet"
          ),
        });

        if (res) {
          setMessages([
            ...messages,
            {
              id: "retry",
              content: "Here is the new response",
              role: "assistant",
            },
            {
              id: "retry-response",
              content: res,
              role: "assistant",
            },
            {
              id: "retry-response-ask",
              content: "What do you think about this response?",
              role: "assistant",
            },
          ]);
        }
      };

      const timer = setTimeout(() => {
        handleRetry();
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [tools]);

  return (
    <div className="flex flex-col grow">
      <section className=" flex flex-grow h-2">
        <div className="flex grow">
          <div className=" flex flex-grow flex-col pt-3 border-r justify-center items-center max-w-2xl">
            <div className=" max-w-lg flex-grow flex flex-col w-full">
              <div
                id="messages"
                className="flex flex-col flex-grow gap-2 overflow-y-scroll h-3"
              >
                {messages.map((m: Message) => {
                  return (
                    <MessageComponent
                      m={m}
                      key={m.content}
                      tenantId={tenant ?? ""}
                    />
                  );
                })}
                {isTyping && <MessageLoading />}
                <div className="h-10 min-h-4 mt-4" ref={bottomRef}></div>
              </div>
            </div>

            <AIInputWithLoading
              onSubmit={(value) => {
                append({ content: value, role: "user" });
              }}
              isLoading={isLoading}
              loadingDuration={3000}
              thinkingDuration={2000}
              placeholder="Ask me a question"
              step={
                processMapper?.[
                  (step?.step as keyof typeof processMapper) ?? "none"
                ] ?? ""
              }
              stepMetadata={step?.metadata}
              messages={messages.length}
            />
          </div>
        </div>
      </section>
    </div>
  );
};
